import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { fetchChecklists, fetchChecklistTopicStatus, markChecklistAsRead } from "../services/checklistService";

interface ChecklistContextType {
  checklistData: any[];
  checklistTopicStatus: any[];
  markChecklistAsReadContext: (checklistId: string, status: string) => Promise<void>; // Change status to string
}

const ChecklistContext = createContext<ChecklistContextType | undefined>(undefined);

export const ChecklistProvider = ({ children }: { children: ReactNode }) => {
  const { authState } = useOktaAuth();
  const [checklistData, setChecklistData] = useState<any[]>([]);
  const [checklistTopicStatus, setChecklistTopicStatus] = useState<any[]>([]);
  const oktaAccessToken = authState?.accessToken?.accessToken;

  useEffect(() => {
    if (!oktaAccessToken) return;

    const getChecklistData = async () => {
      // Get checklist API
      const data = await fetchChecklists(oktaAccessToken);
      setChecklistData(data);
      // Get checklist/all status API
      const topicStatus = await fetchChecklistTopicStatus(oktaAccessToken);
      setChecklistTopicStatus(topicStatus);
    };

    getChecklistData();
  }, [oktaAccessToken]);

  // Function to mark checklist as read - Update API
  const markChecklistAsReadContext_Update = async (checklistId: string, status: string) => { // Ensure status is string
    if (!oktaAccessToken) {
      console.error("No valid token found");
      return;
    }

    try {
      const numericChecklistId = Number(checklistId); // Convert checklistId to number
      console.log("Sending payload:", { checklistId: numericChecklistId, status });
      // Call API with correct status
      const response = await markChecklistAsRead(oktaAccessToken, numericChecklistId, status);
      console.log("API Response:", response);

      // Ensure state updates correctly based on API response
      if (response?.status === status) {
        setChecklistTopicStatus((prev) =>
          prev.map((item) =>
            item.id === checklistId ? { ...item, status } : item
          )
        );
      } else {
        console.warn("API response mismatch. Fetching updated checklist status...");
        const updatedChecklist = await fetchChecklistTopicStatus(oktaAccessToken);
        setChecklistTopicStatus(updatedChecklist);
      }
    } catch (error) {
      console.error("Error updating checklist status:", error);
    }
  };

    // Function to mark checklist as read - Insert/Update API (PUT)
  const markChecklistAsReadContext = async (checklistId: number | string, status: string) => {
    if (!oktaAccessToken) {
      console.error("No valid token found in checklist API");
      return;
    }
    try {
      const numericChecklistId = Number(checklistId);
      console.log("Sending payload:", { checklistId: numericChecklistId, status });
  
      // Send PUT request (insert or update based on backend logic)
      const response = await markChecklistAsRead(oktaAccessToken, numericChecklistId, status);
      console.log("API Response:", response);
  
      // Check if this checklistId already exists in local state
      const exists = checklistTopicStatus.some(item => item.checklistId === numericChecklistId);
  
      if (response?.status === status) {
        setChecklistTopicStatus(prevStatus => {
          if (exists) {
            return prevStatus.map(item =>
              item.checklistId === numericChecklistId ? { ...item, status } : item
            );
          } else {
            return [...prevStatus, { checklistId: numericChecklistId, status }];
          }
        });
      } else {
        // API might have returned unexpected response — fallback: refetch full status list
        console.warn("Unexpected response. Refetching all checklist statuses...");
        const updatedChecklist = await fetchChecklistTopicStatus(oktaAccessToken);
        setChecklistTopicStatus(updatedChecklist);
      }
  
    } catch (error) {
      console.error("Error inserting/updating checklist status:", error);
    }
  };
  

  return (
    <ChecklistContext.Provider value={{ checklistData, checklistTopicStatus, markChecklistAsReadContext }}>
      {children}
    </ChecklistContext.Provider>
  );
};

export const useChecklist = () => {
  const context = useContext(ChecklistContext);
  if (!context) {
    throw new Error("useChecklist must be used within a ChecklistProvider");
  }
  return context;
};
